import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, addDays, startOfDay } from 'date-fns';
import { ko } from 'date-fns/locale';
import { CalendarDays, Filter, Search } from 'lucide-react';
import api from '@/lib/api';
import ScheduleListItem from '@/components/ScheduleListItem';
import ExternalScheduleCard from '@/components/ExternalScheduleCard';
import AdSlot from '@/components/AdSlot';
import { bucketByDisplayStatus, groupPastByHour } from '@/lib/scheduleDisplay';
import { LIVEMOA_CATEGORY_OPTIONS, LIVEMOA_CATEGORY_LABEL, normalizeLivemoaCategory } from '@/lib/livemoaCategory';
// STEP KAKAO-3 (2026-09-10) · External LIVE UI restricted to Naver + Kakao only.
// Registry / DB / Collector code for other platforms stays intact — this is a
// UI-only allowlist (§8 · §9 · §15 · §16 forbids code/data deletion).
const VISIBLE_EXTERNAL_PLATFORMS = ['naver_shopping_live', 'kakao_shopping_live'];
const PLATFORM_LABEL = {
  // SNS (internal live_schedules · unchanged)
  tiktok: '틱톡', instagram: '인스타그램', youtube: '유튜브',
  // External shopping LIVE — only these two are visible after KAKAO-3.
  naver_shopping_live: '네이버 쇼핑라이브',
  kakao_shopping_live: '카카오 쇼핑라이브',
};

// External-LIVE-visible platforms only.  SSG / GS / Grip / Coupang / etc.
// remain in the backend registry but never surface as filter chips (§9).
const PLATFORM_OPTIONS = [
  'all',
  'tiktok', 'instagram', 'youtube',              // SNS internal
  'naver_shopping_live', 'kakao_shopping_live',  // External shopping
];
// Round 5b (2026-09-10) · CATEGORY_OPTIONS · Master 10-category taxonomy
// sourced from `livemoaCategory.js` (REAL Kakao DB audit).  Legacy english
// keys (`fashion`, `beauty`, …) are retired — Schedule now unifies with
// LiveOnly / Home on the same Korean master labels.
const CATEGORY_OPTIONS = LIVEMOA_CATEGORY_OPTIONS;

// Phase 64 · STEP KAKAO-3-UX (2026-09-10) — Homeshopping chip removed from
// the user-facing filter per §1.  Backend collectors (`cj_onstyle`,
// `gs_shop`, `gongyoung`, `ssg_live` etc.) remain in the registry / DB /
// Cron intact; only the UI chip is hidden until the homeshopping trio is
// re-validated in a later STEP.  §31: existing collector code MUST NOT be
// deleted.
const SOURCE_CHIPS = [
  { key: 'all',           label: '전체' },
  { key: 'sns',           label: 'SNS LIVE' },
  { key: 'shopping',      label: '쇼핑 LIVE' },
];

const Schedule = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = searchParams.get('date') || format(new Date(), 'yyyy-MM-dd');
  const highlightId = searchParams.get('broadcast');

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedPlatform, setSelectedPlatform] = useState(searchParams.get('platform') || 'all');
  const [selectedSource, setSelectedSource] = useState(searchParams.get('source') || 'all');
  const [searchQ, setSearchQ] = useState(searchParams.get('q') || '');
  const [lives, setLives] = useState([]);        // INTERNAL (LIVEMOA seller LIVE)
  const [externals, setExternals] = useState([]); // EXTERNAL (Phase 64)
  const [loading, setLoading] = useState(true);
  // Round 2 §6·§7·§8 — per-section [더보기] state · reset on date/filter change.
  const [showAll, setShowAll] = useState({ live: false, todayUpcoming: false, future: false, past: false });
  const highlightRef = useRef(highlightId);

  // Quick date chips — today + next 6 days
  const dateChips = useMemo(() => {
    const base = startOfDay(new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(base, i);
      return { iso: format(d, 'yyyy-MM-dd'), label: format(d, 'M.d (EEE)', { locale: ko }) };
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Round 2 §7 — filter/date change resets 더보기.
    setShowAll({ live: false, todayUpcoming: false, future: false, past: false });
    (async () => {
      // Round 5b (2026-09-10) · pass `q` to external endpoint so backend
      // pre-filters (title / seller_name / category / product_name).  Internal
      // SNS LIVE is filtered client-side below since it has its own schema.
      const externalParams = { date: selectedDate, limit: 200 };
      if (searchQ.trim()) externalParams.q = searchQ.trim();
      const [internalRes, externalRes] = await Promise.allSettled([
        api.get('/home/today-schedule', { params: { date: selectedDate, limit: 200 } }),
        api.get('/schedule/external',   { params: externalParams }),
      ]);
      if (cancelled) return;
      setLives(internalRes.status === 'fulfilled' ? (internalRes.value.data || []) : []);
      setExternals(externalRes.status === 'fulfilled' ? (externalRes.value.data?.items || []) : []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [selectedDate, searchQ]);

  // Persist filters/date in URL
  useEffect(() => {
    const params = {};
    if (selectedDate) params.date = selectedDate;
    if (selectedCategory !== 'all') params.category = selectedCategory;
    if (selectedPlatform !== 'all') params.platform = selectedPlatform;
    if (selectedSource !== 'all') params.source = selectedSource;
    if (searchQ.trim()) params.q = searchQ.trim();
    if (highlightRef.current) params.broadcast = highlightRef.current;
    setSearchParams(params, { replace: true });
  }, [selectedDate, selectedCategory, selectedPlatform, selectedSource, searchQ, setSearchParams]);

  // Scroll to highlighted broadcast once list is rendered
  useEffect(() => {
    if (!highlightId || loading) return;
    const el = document.getElementById(`broadcast-${highlightId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-[#FF3B30]');
      const t = setTimeout(() => el.classList.remove('ring-2', 'ring-[#FF3B30]'), 2500);
      return () => clearTimeout(t);
    }
  }, [highlightId, loading, lives]);

  // Filter INTERNAL (SNS platforms → source='sns')
  const filteredInternal = useMemo(() => {
    if (selectedSource !== 'all' && selectedSource !== 'sns') return [];
    const needle = searchQ.trim().toLowerCase();
    return lives.filter((live) => {
      if (selectedCategory !== 'all' && normalizeLivemoaCategory(live) !== selectedCategory) return false;
      if (selectedPlatform !== 'all' && live.platform !== selectedPlatform) return false;
      if (needle) {
        const hay = ((live.title || '') + ' ' + (live.seller_name || '') + ' ' + (live.category || '') + ' ' + (live.product_name || '')).toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [lives, selectedCategory, selectedPlatform, selectedSource, searchQ]);

  // Filter EXTERNAL by source category + platform + master category.
  // STEP KAKAO-3 (2026-09-10) · Only Naver + Kakao are surfaced to end
  // users regardless of what the backend returns (§8 · §15).  Other
  // registered external platforms (SSG / GS / Gmarket / CJ Onstyle /
  // Gongyoung / manual) remain fetchable via API but never render.
  // Round 5b (2026-09-10) · `selectedCategory` now uses the 10 master
  // labels — client-side `normalizeLivemoaCategory` maps each row.
  const filteredExternal = useMemo(() => {
    if (selectedSource === 'sns') return [];
    return externals.filter((e) => {
      if (!VISIBLE_EXTERNAL_PLATFORMS.includes(e.platform)) return false;
      if (selectedSource !== 'all' && e.platform_category !== selectedSource) return false;
      if (selectedPlatform !== 'all' && e.platform !== selectedPlatform) return false;
      if (selectedCategory !== 'all' && normalizeLivemoaCategory(e) !== selectedCategory) return false;
      return true;
    });
  }, [externals, selectedSource, selectedPlatform, selectedCategory]);

  // STEP FINAL: SCHEDULE DISPLAY TIME POLICY (LOCK)
  // Merge INTERNAL SNS + EXTERNAL shopping into one stream and bucket by
  // the shared +60min rule so the same status appears everywhere. Each
  // row is tagged with `_kind` so we can render the right component.
  const buckets = useMemo(() => {
    const merged = [
      ...filteredInternal.map((it) => ({ ...it, _kind: 'internal' })),
      ...filteredExternal.map((it) => ({ ...it, _kind: 'external' })),
    ];
    return bucketByDisplayStatus(merged);
  }, [filteredInternal, filteredExternal]);

  const pastGroups = useMemo(() => groupPastByHour(buckets.past), [buckets.past]);

  const renderRow = (item) => (item._kind === 'external'
    ? <ExternalScheduleCard key={`ext-${item.external_id}`} item={item} />
    : <ScheduleListItem key={`int-${item.schedule_id}`} live={item} rowLinkMode="seller" />);

  const [pastOpen, setPastOpen] = useState(false);
  const totalCount = filteredInternal.length + filteredExternal.length;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="text-[#FF3B30]" size={22} />
            <h1
              className="text-3xl sm:text-4xl font-black tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="schedule-title"
            >
              편성표
            </h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            LIVEMOA SNS LIVE + 국내 라이브쇼핑 통합 편성표
          </p>
        </div>

        {/* Ad slot — top of /schedule (managed via /admin/ads) */}
        <div className="mb-4"><AdSlot slotCode="schedule_top" /></div>

        {/* Date chips */}
        <div
          className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          data-testid="date-chips"
        >
          {dateChips.map((d) => (
            <button
              key={d.iso}
              type="button"
              onClick={() => setSelectedDate(d.iso)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                selectedDate === d.iso
                  ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                  : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-[#FF3B30]'
              }`}
              data-testid={`date-chip-${d.iso}`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Source chips (SNS / Shopping / Homeshopping) */}
        <div className="mt-3 flex gap-2 flex-wrap" data-testid="source-chips">
          {SOURCE_CHIPS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setSelectedSource(s.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                selectedSource === s.key
                  ? 'bg-black text-white dark:bg-white dark:text-black border-transparent'
                  : 'bg-white dark:bg-[#141414] text-gray-600 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-black/40'
              }`}
              data-testid={`source-chip-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Round 5b (2026-09-10) · Search input · title/seller/category/product */}
        <div className="relative mt-3" data-testid="schedule-search-wrap">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="방송 · 상품 · 판매자 검색"
            className="w-full pl-9 pr-3 py-2.5 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-sm focus:outline-none focus:border-[#FF3B30]"
            data-testid="schedule-search"
          />
        </div>

        {/* Round 5b (2026-09-10) · Master 10-category chips · horizontal scroll */}
        <div
          className="mt-3 flex gap-2 overflow-x-auto -mx-4 px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="schedule-cats"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setSelectedCategory(c)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                selectedCategory === c
                  ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                  : 'bg-white dark:bg-[#141414] text-gray-600 dark:text-gray-300 border-black/10 dark:border-white/10 hover:border-[#FF3B30]'
              }`}
              data-testid={`schedule-cat-${c}`}
            >
              {LIVEMOA_CATEGORY_LABEL[c] || c}
            </button>
          ))}
        </div>

        {/* Filters — Platform select + total count */}
        <div className="mt-3 flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 dark:text-gray-400">
            <Filter size={13} /> 필터
          </span>
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
            className="px-3 py-1.5 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-full text-sm"
            data-testid="platform-filter"
          >
            {PLATFORM_OPTIONS.map((p) => (
              <option key={p} value={p}>{p === 'all' ? '모든 플랫폼' : (PLATFORM_LABEL[p] || p)}</option>
            ))}
          </select>
          <span className="ml-auto text-xs text-gray-500 dark:text-gray-400" data-testid="schedule-count">
            총 {totalCount}건
          </span>
        </div>

        {/* Vertical schedule list */}
        <div className="mt-6 flex flex-col gap-2 sm:gap-2.5" data-testid="schedule-list">
          {loading && (
            <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="schedule-loading">
              편성표를 불러오는 중…
            </div>
          )}
          {!loading && totalCount === 0 && (
            <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400" data-testid="schedule-empty">
              선택한 조건에 해당하는 방송이 없습니다.
            </div>
          )}

          {/* INTERNAL rows are bucketed together with EXTERNAL below. */}

          {/* LIVE — 지금 방송 중 (Round 2 §6 · initial 6 · [더보기]) */}
          {!loading && buckets.live.length > 0 && (
            <div className="mt-2 mb-1 flex items-center gap-2 text-xs font-bold text-[#FF3B30]" data-testid="section-live">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
              <span>지금 LIVE ({buckets.live.length})</span>
              <span className="flex-1 h-px bg-black/10 dark:bg-white/10" />
            </div>
          )}
          {!loading && buckets.live.slice(0, showAll.live ? buckets.live.length : 6).map(renderRow)}
          {!loading && buckets.live.length > 6 && !showAll.live && (
            <button type="button" onClick={() => setShowAll(s => ({...s, live: true}))}
              className="mt-1 mb-1 self-center px-4 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold text-gray-600 dark:text-gray-300 hover:border-[#FF3B30] hover:text-[#FF3B30]"
              data-testid="more-live">
              지금 LIVE 더보기 ↓
            </button>
          )}

          {/* TODAY UPCOMING — 오늘 예정 (Round 2 §7 · initial 12 · [더보기]) */}
          {!loading && buckets.todayUpcoming.length > 0 && (
            <div className="mt-3 mb-1 flex items-center gap-2 text-xs font-bold text-gray-600 dark:text-gray-400" data-testid="section-today">
              <span>🕐 오늘 예정 방송 ({buckets.todayUpcoming.length})</span>
              <span className="flex-1 h-px bg-black/10 dark:bg-white/10" />
            </div>
          )}
          {!loading && buckets.todayUpcoming.slice(0, showAll.todayUpcoming ? buckets.todayUpcoming.length : 12).map(renderRow)}
          {!loading && buckets.todayUpcoming.length > 12 && !showAll.todayUpcoming && (
            <button type="button" onClick={() => setShowAll(s => ({...s, todayUpcoming: true}))}
              className="mt-1 mb-1 self-center px-4 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold text-gray-600 dark:text-gray-300 hover:border-[#FF3B30] hover:text-[#FF3B30]"
              data-testid="more-today-upcoming">
              오늘 예정 방송 더보기 ↓
            </button>
          )}

          {/* FUTURE — 방송 예정 (Round 2 §2·§7 · label 변경 · initial 12 · [더보기]) */}
          {!loading && buckets.future.length > 0 && (
            <div className="mt-3 mb-1 flex items-center gap-2 text-xs font-bold text-gray-600 dark:text-gray-400" data-testid="section-future">
              <span>🕐 방송 예정 ({buckets.future.length})</span>
              <span className="flex-1 h-px bg-black/10 dark:bg-white/10" />
            </div>
          )}
          {!loading && buckets.future.slice(0, showAll.future ? buckets.future.length : 12).map(renderRow)}
          {!loading && buckets.future.length > 12 && !showAll.future && (
            <button type="button" onClick={() => setShowAll(s => ({...s, future: true}))}
              className="mt-1 mb-1 self-center px-4 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold text-gray-600 dark:text-gray-300 hover:border-[#FF3B30] hover:text-[#FF3B30]"
              data-testid="more-future">
              방송 예정 더보기 ↓
            </button>
          )}

          {/* PAST — 지난 방송 (Round 2 §8 · initial 6 · [더보기] · stale LIVE 제외 정책 REUSE) */}
          {!loading && buckets.past.length > 0 && (
            <>
              <div className="mt-4 mb-1 flex items-center gap-2 text-xs font-bold text-gray-500 dark:text-gray-400" data-testid="section-past">
                <span>↻ 지난 방송 ({buckets.past.length})</span>
                <span className="flex-1 h-px bg-black/10 dark:bg-white/10" />
              </div>
              {buckets.past.slice(0, showAll.past ? buckets.past.length : 6).map(renderRow)}
              {buckets.past.length > 6 && !showAll.past && (
                <button type="button" onClick={() => setShowAll(s => ({...s, past: true}))}
                  className="mt-1 mb-1 self-center px-4 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold text-gray-600 dark:text-gray-300 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  data-testid="more-past">
                  지난 방송 더보기 ↓
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Schedule;
